import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Text, Icon, Spacing } from '@reservamos/elements';
import LocalizedCurrency from 'components/LocalizedCurrency';
import openTicketETN from '../../images/brands-unique/etn/openTicketETN.svg';
import openTicketGFA from '../../images/brands-unique/gfa/open-ticket.svg';
import 'styles/components/search/OpenTicketButton';
import WalletPrice from './WalletPrice';

const openTicketIcons = {
  etn: openTicketETN,
  gho: openTicketETN,
  costaline: openTicketETN,
  gfa: openTicketGFA,
  default: 'Ticket',
};

/**
 * Renders the OpenTicketButton component.
 *
 * @param {Object} props - The component props.
 * @param {Function} props.selectTrip - The function to select a trip.
 * @param {number} props.total - The total price.
 * @param {Object} props.trip - The trip object.
 * @param {boolean} props.isExchange - Indicates if it is an exchange.
 * @param {number} props.walletPoints - The wallet points.
 * @param {boolean} props.showWalletPoints - Indicates if the wallet price should be shown.
 * @param {string} props.walletType - The wallet type.
 * @returns {JSX.Element} The rendered OpenTicketButton component.
 */
const OpenTicketButton = ({
  selectTrip,
  total,
  trip,
  isExchange,
  walletPoints,
  showWalletPoints,
  walletType,
}) => {
  const { features, env } = useSelector((state) => state.whitelabelConfig);
  const { t } = useTranslation();

  const context = isExchange && 'exchange';
  const message = features.MULTI_OPEN_TICKETS
    ? t('general:open_ticket_from', { context })
    : t('general:open_ticket', { context });
  const openTicketIcon = openTicketIcons[env.brand] || openTicketIcons.default;

  const price = !isExchange && !showWalletPoints && (
    <LocalizedCurrency
      price={total}
      decimals={1}
      color="grayStrong"
      size="L"
      mobileSize="L"
      weight="bold"
      currencyStyle="currency"
    />
  );

  const ticketInformation = (
    <>
      <div className="result-button-wrapper">
        <Spacing size="XS" alignItems="center">
          <Icon type={openTicketIcon} />
          <span className="result-button-copy">
            <Text size="S" weight="semibold" mobileSize="S">
              {message}
            </Text>
          </span>
        </Spacing>
      </div>

      <span className="result-button-purchase">
        <span className="result-button-price">
          {price}
          {showWalletPoints && <WalletPrice walletType={walletType} walletTotal={walletPoints} />}
        </span>
      </span>
    </>
  );

  return (
    <button
      type="button"
      className="result-button"
      data-testid="open-ticket-button"
      onClick={() => selectTrip(trip)}
    >
      {ticketInformation}
    </button>
  );
};

OpenTicketButton.propTypes = {
  selectTrip: PropTypes.func.isRequired,
  total: PropTypes.number.isRequired,
  trip: PropTypes.shape().isRequired,
  isExchange: PropTypes.bool.isRequired,
  walletPoints: PropTypes.number.isRequired,
  showWalletPoints: PropTypes.bool.isRequired,
  walletType: PropTypes.string,
};

OpenTicketButton.defaultProps = {
  walletType: undefined,
};

export default OpenTicketButton;
